import { PageHero } from "@/components/sections/PageHero";
import { Container } from "@/components/ui/Container";
import { company } from "@/content/company";

// Mesmo conteúdo de src/app/privacidade/page.tsx, como componente reutilizável
// pelo bundle do WordPress (o texto aqui não é editável pelo admin do WP —
// para isso, ver a observação no README do tema).

const sections = [
  { id: "quem-trata", title: "1. Quem trata os dados" },
  { id: "dados-tratados", title: "2. Dados que podem ser tratados" },
  { id: "finalidade", title: "3. Para que usamos essas informações" },
  { id: "bases-legais", title: "4. Bases legais" },
  { id: "cookies", title: "5. Cookies e armazenamento local" },
  { id: "compartilhamento", title: "6. Compartilhamento e serviços externos" },
  { id: "retencao-seguranca", title: "7. Retenção e segurança" },
  { id: "direitos", title: "8. Direitos do titular" },
  { id: "identificacao", title: "9. Identificação da empresa" },
  { id: "atualizacoes", title: "10. Atualizações desta política" },
];

export function PrivacidadeContent() {
  return (
    <>
      <PageHero
        eyebrow="Legal"
        title="Política de privacidade"
        description="Como a SAT Labs coleta, usa e protege dados pessoais neste site, em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018 — LGPD)."
      />

      <section className="py-16 sm:py-24">
        <Container className="grid gap-12 lg:grid-cols-[220px_1fr]">
          <nav aria-label="Sumário" className="hidden lg:block">
            <p className="mb-4 font-mono text-xs uppercase tracking-[0.12em] text-text-muted">
              Sumário
            </p>
            <ul className="space-y-2.5 border-l border-line pl-4 text-sm">
              {sections.map((s) => (
                <li key={s.id}>
                  <a href={`#${s.id}`} className="text-text-secondary hover:text-accent-lime">
                    {s.title}
                  </a>
                </li>
              ))}
            </ul>
          </nav>

          <div className="max-w-3xl space-y-10 text-sm leading-relaxed text-text-secondary">
            <p>
              Esta política explica, de forma direta, quais dados a SAT Labs trata quando você
              usa este site — em especial ao preencher o formulário de cadastro ou aceitar o
              aviso de cookies — e quais direitos você tem sobre esses dados.
            </p>

            <div id="quem-trata" className="scroll-mt-24 space-y-3">
              <h2 className="text-lg font-bold uppercase text-text-primary">
                1. Quem trata os dados
              </h2>
              <p>
                A SAT Labs é a responsável pelo tratamento dos dados pessoais coletados por este
                site. Dúvidas sobre esta política ou sobre o tratamento dos seus dados podem ser
                enviadas para{" "}
                <a href={`mailto:${company.email}`} className="text-accent-lime">
                  {company.email}
                </a>
                .
              </p>
            </div>

            <div id="dados-tratados" className="scroll-mt-24 space-y-3">
              <h2 className="text-lg font-bold uppercase text-text-primary">
                2. Dados que podem ser tratados
              </h2>
              <ul className="list-disc space-y-2 pl-5">
                <li>
                  <strong className="text-text-primary">Dados enviados por você:</strong> nome,
                  e-mail, telefone, empresa, estado, tipo de operação, áreas de interesse e
                  mensagem, informados no formulário de cadastro.
                </li>
                <li>
                  <strong className="text-text-primary">Progresso do cadastro:</strong> após sua
                  autorização, as etapas preenchidas podem ser salvas mesmo que o formulário não
                  seja concluído, para permitir contato comercial e medir o funil de atendimento.
                  Antes da autorização, registramos apenas eventos anônimos de abertura.
                </li>
                <li>
                  <strong className="text-text-primary">Registros técnicos de acesso:</strong>{" "}
                  informações padrão gerada pela navegação, como endereço IP, tipo de navegador e
                  data/hora de acesso, coletadas automaticamente pela infraestrutura que hospeda o
                  site.
                </li>
                <li>
                  <strong className="text-text-primary">Preferências salvas localmente:</strong>{" "}
                  se você já viu o aviso de cookies ou a animação de abertura do site, guardado no
                  seu próprio navegador (ver seção 5).
                </li>
              </ul>
            </div>

            <div id="finalidade" className="scroll-mt-24 space-y-3">
              <h2 className="text-lg font-bold uppercase text-text-primary">
                3. Para que usamos essas informações
              </h2>
              <ul className="list-disc space-y-2 pl-5">
                <li>Responder ao contato comercial e apresentar diagnóstico ou proposta.</li>
                <li>Viabilizar o cadastro e o relacionamento comercial solicitado.</li>
                <li>Identificar cadastros interrompidos e melhorar o processo de atendimento.</li>
                <li>Cumprir obrigações legais e regulatórias, quando aplicável.</li>
                <li>Manter o site funcionando corretamente e melhorar a experiência de uso.</li>
              </ul>
            </div>

            <div id="bases-legais" className="scroll-mt-24 space-y-3">
              <h2 className="text-lg font-bold uppercase text-text-primary">4. Bases legais</h2>
              <p>
                O tratamento se apoia, conforme o caso, no seu{" "}
                <strong className="text-text-primary">consentimento</strong> (ao autorizar o
                armazenamento dos dados no formulário ou aceitar o aviso de cookies), na{" "}
                <strong className="text-text-primary">execução de procedimentos preliminares</strong>{" "}
                a um possível contrato, no{" "}
                <strong className="text-text-primary">cumprimento de obrigação legal</strong> e no{" "}
                <strong className="text-text-primary">legítimo interesse</strong> da SAT Labs em
                manter o site seguro e funcional — sempre de forma proporcional à finalidade.
              </p>
            </div>

            <div id="cookies" className="scroll-mt-24 space-y-3">
              <h2 className="text-lg font-bold uppercase text-text-primary">
                5. Cookies e armazenamento local
              </h2>
              <p>
                Este site usa apenas armazenamento local essencial ao seu funcionamento — não
                usamos cookies de publicidade nem venda de dados a terceiros para fins
                publicitários. Guardamos, no seu navegador:
              </p>
              <ul className="list-disc space-y-2 pl-5">
                <li>
                  <code className="rounded bg-bg-raised px-1.5 py-0.5 font-mono text-xs text-text-primary">
                    satlabs-privacy-ack
                  </code>{" "}
                  — registra que você já viu este aviso, para não exibi-lo novamente.
                </li>
                <li>
                  <code className="rounded bg-bg-raised px-1.5 py-0.5 font-mono text-xs text-text-primary">
                    satlabs-intro-seen
                  </code>{" "}
                  — evita repetir a animação de abertura a cada visita, durante a mesma sessão.
                </li>
              </ul>
              <p>
                Você pode apagar esses dados a qualquer momento nas configurações do seu
                navegador; isso apenas faz o aviso e a animação reaparecerem.
              </p>
            </div>

            <div id="compartilhamento" className="scroll-mt-24 space-y-3">
              <h2 className="text-lg font-bold uppercase text-text-primary">
                6. Compartilhamento e serviços externos
              </h2>
              <p>
                Não vendemos dados pessoais a terceiros. Os dados do formulário de cadastro podem
                ser processados por ferramentas de e-mail e CRM usadas internamente pela SAT Labs
                para gestão do relacionamento comercial. Links para WhatsApp, Instagram e LinkedIn
                levam a serviços de terceiros com políticas de privacidade próprias, que não são
                controladas pela SAT Labs.
              </p>
            </div>

            <div id="retencao-seguranca" className="scroll-mt-24 space-y-3">
              <h2 className="text-lg font-bold uppercase text-text-primary">
                7. Retenção e segurança
              </h2>
              <p>
                Os dados são mantidos apenas pelo tempo necessário para cumprir as finalidades
                descritas nesta política ou por exigência legal. Adotamos medidas técnicas e
                administrativas razoáveis para proteger os dados contra acesso não autorizado,
                perda ou uso indevido.
              </p>
            </div>

            <div id="direitos" className="scroll-mt-24 space-y-3">
              <h2 className="text-lg font-bold uppercase text-text-primary">
                8. Direitos do titular
              </h2>
              <p>Conforme a LGPD, você pode solicitar, a qualquer momento:</p>
              <ul className="list-disc space-y-2 pl-5">
                <li>Confirmação da existência de tratamento e acesso aos seus dados.</li>
                <li>Correção de dados incompletos, inexatos ou desatualizados.</li>
                <li>Anonimização, bloqueio ou eliminação de dados desnecessários ou excessivos.</li>
                <li>Portabilidade dos dados a outro fornecedor, mediante requisição expressa.</li>
                <li>Eliminação dos dados tratados com base no seu consentimento.</li>
                <li>Revogação do consentimento, a qualquer momento.</li>
              </ul>
              <p>
                Para exercer qualquer um desses direitos, escreva para{" "}
                <a href={`mailto:${company.email}`} className="text-accent-lime">
                  {company.email}
                </a>
                .
              </p>
            </div>

            <div id="identificacao" className="scroll-mt-24 space-y-3">
              <h2 className="text-lg font-bold uppercase text-text-primary">
                9. Identificação da empresa
              </h2>
              <p>
                Razão social, CNPJ e endereço físico completos estão em processo de confirmação
                com a SAT Labs e serão publicados nesta página assim que validados —{" "}
                <span className="text-status-warn">informação pendente de validação</span>.
              </p>
            </div>

            <div id="atualizacoes" className="scroll-mt-24 space-y-3">
              <h2 className="text-lg font-bold uppercase text-text-primary">
                10. Atualizações desta política
              </h2>
              <p>
                Esta política pode ser atualizada para refletir mudanças no site ou na legislação.
                Alterações relevantes serão publicadas nesta página com a data de atualização.
              </p>
              <p className="text-xs text-text-muted">Última atualização: julho de 2026.</p>
            </div>
          </div>
        </Container>
      </section>
    </>
  );
}
